import { Injectable } from '@angular/core';
import { PreferenceStorageService } from '@app/shared/services/preference-storage.service';
import { registerWithGlobalApp } from '@app/utils/app';
import { groupBy, sortBy } from '@app/utils/common';
import { equals } from '@app/utils/equals';
import { loggerFor } from '@app/utils/logger';
import { normalizeSortOrder, SortOrder, SortOrderArray, SortOrderObject } from '@app/utils/sort/compare';
import { getUnixTime } from 'date-fns';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * Service that can be used to sort items by usage.
 */
@Injectable({
  providedIn: 'root',
})
export class RecentlyUsedService {
  entries: RecentlyUsedMap = {};

  private log = loggerFor('RecentlyUsedService');

  private _entries$: Subject<RecentlyUsedMap> = new BehaviorSubject({});

  get entries$(): Observable<RecentlyUsedMap> {
    return this._entries$.asObservable();
  }

  constructor(private storageService: PreferenceStorageService) {
    this.storageService.preference$<RecentlyUsedMap>('recently-used').subscribe((entries) => {
      this.entries = entries ?? {};
      this._entries$.next(this.entries);
    });

    if (environment.production === false) {
      registerWithGlobalApp('clearRecentlyUsed', (type?: string) => this.reset(type));
    }
  }

  /**
   * Update the recently used history for the given entry.
   */
  add<T>({ historyName, id, max, meta, routeParameters }: Options<T>): void {
    this.log.debug(`Add recently used. key=${historyName}, id=${id}`);

    if (this.entries[historyName] == null) {
      this.entries[historyName] = [];
    }

    const entries = this.entries[historyName].slice() as RecentlyUsedEntry[];

    const maybeEntry = this.entries[historyName].find(
      (entry) => entry.id === id && equals(entry.routeParameters, routeParameters)
    );

    if (maybeEntry != null) {
      maybeEntry.count++;
      maybeEntry.timestamp = getUnixTime(new Date());
    } else {
      entries.push({
        count: 1,
        id,
        meta,
        routeParameters,
        timestamp: getUnixTime(new Date()),
      });
    }

    entries.sort(sortBy([['timestamp', 'desc']]));

    if (max != null) {
      this.entries[historyName] = entries.slice(0, max);
    } else {
      this.entries[historyName] = entries;
    }

    this.persist();
  }

  /**
   * Clear the entire history. If a prefix is passed, all
   * entries starting with that prefix will be deleted.
   *
   * @param prefix Remove only entries that begin with this prefix.
   */
  clear(prefix?: string): void {
    if (prefix != null) {
      Object.keys(this.entries)
        .filter((key) => key.startsWith(prefix))
        .forEach((key) => {
          delete this.entries[key];
        });
    } else {
      this.entries = {};
    }

    this.persist();
  }

  /**
   * Get an observable that contains the current list of recently used entries for the given history name.
   * @param historyName Name of the history.
   */
  list$(historyName: string): Observable<RecentlyUsedEntry[]> {
    return this.entries$.pipe(
      map((entryMap) => {
        return entryMap[historyName] ?? [];
      })
    );
  }

  /**
   * Get all recently used entries for the given history name.
   * @param historyName Name of the history.
   */
  list<T = unknown>(historyName: string): RecentlyUsedEntry<T>[] {
    return this.entries[historyName] ?? [];
  }

  /**
   * Remove a specific entry.
   * @param historyName Name of the history containing the entry.
   * @param id Id of the entry.
   */
  remove(historyName: string, id: number | string): void {
    this.entries[historyName] = this.list(historyName).filter((entry) => entry.id !== id);

    this.persist();
  }

  /**
   * Reset specific history.
   * @param historyName Name of the history.
   */
  reset(historyName: string): void {
    delete this.entries[historyName];

    this.persist();
  }

  /**
   * Sort items by use (last-used or most-used).
   */
  sort<T>({ compare, equalSortOrder = [], historyName, items, order }: SortOptions<T>): T[] {
    const recentlyUsedEntries = this.list(historyName);
    const { recentlyUsed = [], others = [] } = groupBy(items, (item) => {
      const wasRecentlyUsed = recentlyUsedEntries.some((entry) => compare(item, entry.id));

      return wasRecentlyUsed ? 'recentlyUsed' : 'others';
    });

    const recentlyUsedSorted = recentlyUsed
      .map((item) => {
        let sortValue: number | string;

        if (order === 'last-used') {
          sortValue = recentlyUsedEntries.find((entry) => compare(item, entry.id))?.timestamp;
        } else if (order === 'most-used') {
          sortValue = recentlyUsedEntries.find((entry) => compare(item, entry.id))?.count;
        }

        return {
          item,
          value: sortValue,
        };
      })
      .sort(sortBy([['value', 'desc'], ...this.getAdditionalOrder(equalSortOrder)]))
      .map(({ item }) => item);

    return [...recentlyUsedSorted, ...others.sort(sortBy(equalSortOrder))];
  }

  /**
   * Map the given orders so that they can be applied to an array of recently used items.
   * @param orders Array of orders that the user can pass to sort items.
   */
  private getAdditionalOrder<T, U extends { item: T; value: number }>(
    orders: EqualSortOrder<T>[]
  ): SortOrder<U>[] {
    return orders.map(normalizeSortOrder).map((obj) => {
      let sortOrder: SortOrder<U>;

      if (typeof obj.key === 'function') {
        const key = obj.key;

        sortOrder = {
          ignoreCase: obj.ignoreCase,
          key: ({ item }) => key(item),
          order: obj.order,
        };
      } else {
        sortOrder = {
          ignoreCase: obj.ignoreCase,
          key: `item.${obj.key}`,
          order: obj.order,
        };
      }

      return sortOrder;
    });
  }

  private persist(): void {
    this.storageService.updatePreference('recently-used', this.entries);
    this._entries$.next(this.entries);
  }
}

export type RecentlyUsedMap = Record<string, RecentlyUsedEntry[]>;
export type RecentlyUsedSortOrder = 'last-used' | 'most-used';

export interface RecentlyUsedEntry<T = any> {
  /**
   * How many times the entry was used.
   */
  count: number;

  /**
   * Unique value to identify an entry.
   */
  id: number | string;

  /**
   * Additional information about this entry.
   */
  meta?: T;

  /**
   * Route on which this entry was used.
   */
  routeParameters?: Record<string, string>;

  /**
   * Time when the entry was last used.
   */
  timestamp: number;
}

export interface SortOptions<T> {
  /**
   * Function to identify the correct recently used entry for an item.
   * Should return true if b is the id of *a*
   *
   * @param item Entry of *items*.
   * @param id Id of a recently used entry.
   */
  compare: (item: T, id: number | string) => boolean;

  /**
   * Additional sort order that is applied to items in the group, i.e. in case of:
   *  - 'most-used': items with the same amount of calls
   *  - 'last-used': items that have never been called
   */
  equalSortOrder?: EqualSortOrder<T>[];

  /**
   * Key that is used to look up the recently used history (should be
   * the same key that is passed to {@link RecentlyUsedService.add}).
   */
  historyName: string;

  /**
   * Items to sort.
   */
  items: T[];

  /**
   * Sort order.
   */
  order: RecentlyUsedSortOrder;
}

type EqualSortOrder<T> = SortOrderArray<T> | Omit<SortOrderObject<T>, 'defaultValue'>;

interface Options<T> {
  /**
   * History name to use (this key should be used to sort
   * items when using {@link RecentlyUsedService.sort}).
   */
  historyName: string;

  /**
   * A unique name can be used to identify entries (this will be passed to
   * {@link SortOptions.compare} when calling {@link RecentlyUsedService.sort}).
   */
  id: number | string;

  /**
   * If the number of entries is greater than max, the last used entry that is removed.
   */
  max?: number;

  /**
   * Additional information about this entry (is always updated to the latest value).
   */
  meta?: T;

  /**
   * Additional route parameters that are used to distinguish usage by route.
   */
  routeParameters?: Record<string, string>;
}
